import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon, type IconName } from './Icon'
import { useQuery } from '@tanstack/react-query'
import { useAuth, type PermKey } from '../lib/auth'

interface NavCounts {
  rebills_open: number
  disputes_open: number
  claims_open: number
}

interface DiagState {
  hasErrors:   boolean
  hasWarnings: boolean
}

type Badge = 'rebills' | 'disputes' | 'claims' | 'recovery' | 'live' | 'new' | 'diag'

interface NavItem {
  to:       string
  labelKey: string
  label:    string   // fallback
  icon:     IconName
  badge?:   Badge
  perm?:    PermKey
  anyPerm?: PermKey[]   // shown when the user has any of these
  adminOnly?: boolean   // owner/admin only
}

interface NavSection {
  titleKey: string
  title:    string  // fallback
  items:    NavItem[]
  perm?:    PermKey
}

const SECTIONS: NavSection[] = [
  {
    titleKey: 'nav.workspace',
    title: 'Workspace',
    items: [
      { to: '/dashboard', labelKey: 'nav.dashboard',    label: 'My Dashboard', icon: 'layout-dashboard', badge: 'new' },
      { to: '/pulse',     labelKey: 'nav.pulse',        label: 'Pulse',         icon: 'activity',         badge: 'live', perm: 'pulse' },
      { to: '/ask',       labelKey: 'nav.ask',          label: 'Ask ✨',        icon: 'sparkles',         badge: 'new',  perm: 'ask'   },
    ],
  },
  {
    titleKey: 'nav.analytics',
    title: 'Analytics',
    items: [
      { to: '/income',    labelKey: 'nav.income',    label: 'P&L',        icon: 'bar-chart-2', badge: 'new', perm: 'pulse'  },
      { to: '/cash',      labelKey: 'nav.cash',      label: 'Cash',       icon: 'wallet',  badge: 'new', perm: 'cash'      },
      { to: '/stripe',    labelKey: 'nav.stripe',    label: 'Stripe',     icon: 'credit-card',            perm: 'cash'      },
      { to: '/shipments', labelKey: 'nav.shipments', label: 'Shipments',  icon: 'package', badge: 'new', perm: 'ships'     },
      { to: '/customers', labelKey: 'nav.customers', label: 'Customers',  icon: 'users',   badge: 'new', perm: 'customers' },
      { to: '/pricing',   labelKey: 'nav.pricing',   label: 'Pricing',    icon: 'tag',     badge: 'new', perm: 'pricing'   },
    ],
  },
  {
    titleKey: 'nav.operations',
    title: 'Operations',
    items: [
      { to: '/carriers',          labelKey: 'nav.carriers',          label: 'Carriers',         icon: 'boxes'                              },
      { to: '/affiliates',        labelKey: 'nav.affiliates',        label: 'Affiliates',       icon: 'handshake',    perm: 'affiliates'  },
      { to: '/invoices',          labelKey: 'nav.customer_invoices', label: 'Customer Invoices', icon: 'file-text',    perm: 'invoices' },
      { to: '/carrier-invoices',  labelKey: 'nav.carrier_invoices',  label: 'Carrier Invoices',  icon: 'receipt',      perm: 'invoices' },
      { to: '/carrier-adjustments', labelKey: 'nav.carrier_adjustments', label: 'Carrier Adjustments', icon: 'undo-2', badge: 'rebills', perm: 'recon' },
      // Disputes + Carrier Claims, one page since 2026-10-22
      { to: '/recovery',          labelKey: 'nav.carrier_recovery',  label: 'Carrier Recovery', icon: 'scale',        badge: 'recovery', anyPerm: ['disputes', 'claims'] },
      { to: '/orphans',           labelKey: 'nav.orphans',           label: 'Orphan Shipments', icon: 'package',      perm: 'recon'       },
      { to: '/lookup',            labelKey: 'nav.lookup',            label: 'Tracking Lookup',  icon: 'search'                              },
    ],
  },
  {
    titleKey: 'nav.people_costs',
    title: 'People & Costs',
    items: [
      { to: '/personnel', labelKey: 'nav.personnel',      label: 'Personnel',       icon: 'briefcase',    perm: 'personnel' },
      { to: '/costs',     labelKey: 'nav.operating_costs', label: 'Operating Costs', icon: 'trending-down', perm: 'costs'    },
    ],
  },
  {
    titleKey: 'nav.system',
    title: 'System',
    items: [
      { to: '/uploads',     labelKey: 'nav.activity',        label: 'Activity',        icon: 'cloud-upload',    perm: 'uploads'  },
      { to: '/exports',     labelKey: 'nav.excel_exports',   label: 'Excel Exports',   icon: 'file-spreadsheet', perm: 'exports' },
      { to: '/settings',    labelKey: 'nav.settings',        label: 'Settings',        icon: 'settings',        perm: 'settings' },
      { to: '/users',       labelKey: 'nav.user_management', label: 'User Management', icon: 'user-plus',       perm: 'users'    },
      { to: '/diagnostics', labelKey: 'nav.diagnostics',     label: 'Diagnostics',     icon: 'cpu',             badge: 'diag'    },
      { to: '/activity-log', labelKey: 'nav.activity_log',   label: 'Activity log',    icon: 'history',         adminOnly: true  },
      { to: '/debug-files', labelKey: 'nav.debug_files',     label: 'Debug files',     icon: 'folder-open',     adminOnly: true  },
      { to: '/glossary',    labelKey: 'nav.glossary',        label: 'Glossary 📖',     icon: 'book-open',       badge: 'new'     },
    ],
  },
  {
    titleKey: 'nav.home_legacy',
    title: 'Home (legacy)',
    items: [
      { to: '/home', labelKey: 'nav.executive_home', label: 'Executive Home', icon: 'home' },
    ],
  },
]

export function Sidebar() {
  const { can, isAdmin } = useAuth()
  const { t } = useTranslation()

  const { data: counts } = useQuery<NavCounts>({
    queryKey: ['nav-counts'],
    queryFn: () => fetch('/api/nav/counts').then(r => r.json()),
    staleTime: 30_000,
  })

  const { data: diag } = useQuery<DiagState>({
    queryKey: ['diag-pulse'],
    queryFn: () => fetch('/api/diagnostics').then(r => r.json()),
    refetchInterval: 60_000,
    staleTime:       30_000,
  })

  return (
    <aside
      className="w-64 shrink-0 bg-white border-r border-ink-200 px-3 py-4 overflow-y-auto scrollbar-none"
      style={{ height: 'calc(100vh - 64px)', position: 'sticky', top: '64px' }}
    >
      {SECTIONS.map(section => {
        // Filter items by permission
        const items = section.items.filter(n => (!n.perm || can(n.perm)) && (!n.anyPerm || n.anyPerm.some(p => can(p))) && (!n.adminOnly || isAdmin))
        if (items.length === 0) return null

        return (
          <div key={section.titleKey} className="mb-4">
            <div className="px-2 mb-1">
              <div className="text-[11px] font-semibold text-ink-400 tracking-wider uppercase">
                {t(section.titleKey, section.title)}
              </div>
            </div>
            <nav className="space-y-0.5 text-[13px] font-medium text-ink-700">
              {items.map(n => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  className={({ isActive }) =>
                    `nav-item flex items-center gap-2.5 px-3 py-1.5 rounded-lg cursor-pointer ${isActive ? 'active' : 'hover:bg-ink-50'}`
                  }
                >
                  <Icon name={n.icon} className="w-4 h-4" />
                  <span className="flex-1">{t(n.labelKey, n.label)}</span>
                  {n.badge === 'live' && <span className="pill pill-mint">{t('common.live')}</span>}
                  {n.badge === 'new' && <span className="pill pill-royal text-[9px]">{t('common.new')}</span>}
                  {n.badge === 'rebills' && counts?.rebills_open !== undefined && counts.rebills_open > 0 && (
                    <span className="pill pill-honey">{counts.rebills_open}</span>
                  )}
                  {n.badge === 'disputes' && counts?.disputes_open !== undefined && counts.disputes_open > 0 && (
                    <span className="pill pill-flame">{counts.disputes_open}</span>
                  )}
                  {n.badge === 'claims' && counts?.claims_open !== undefined && counts.claims_open > 0 && (
                    <span className="pill pill-honey">{counts.claims_open}</span>
                  )}
                  {n.badge === 'recovery' && can('disputes') && (counts?.disputes_open ?? 0) > 0 && (
                    <span className="pill pill-flame" title="Open disputes">{counts!.disputes_open}</span>
                  )}
                  {n.badge === 'recovery' && can('claims') && (counts?.claims_open ?? 0) > 0 && (
                    <span className="pill pill-honey" title="Open claims">{counts!.claims_open}</span>
                  )}
                  {n.badge === 'diag' && diag?.hasErrors && (
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                    </span>
                  )}
                  {n.badge === 'diag' && !diag?.hasErrors && diag?.hasWarnings && (
                    <span className="w-2 h-2 rounded-full bg-amber-400" />
                  )}
                </NavLink>
              ))}
            </nav>
          </div>
        )
      })}

      <div className="mt-2 pt-3 border-t border-ink-200 px-3">
        <div className="flex items-center gap-2 text-[11px] text-ink-500">
          {diag?.hasErrors ? (
            <>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
              <span className="text-red-600 font-medium">{t('sidebar.system_error')}</span>
            </>
          ) : diag?.hasWarnings ? (
            <>
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              <span className="text-amber-600">{t('sidebar.warning')}</span>
            </>
          ) : (
            <>
              <span className="w-2 h-2 rounded-full bg-mint-500" />
              <span>{t('sidebar.all_systems_healthy')}</span>
            </>
          )}
          <span className="ml-auto mono">v0.5</span>
        </div>
      </div>
    </aside>
  )
}
